import React, { useEffect, useState } from 'react';
import {
  CreditCard,
  UserCheck,
  ArrowRight,
  LogOut,
  Bell,
  Settings,
  Wallet,
  RefreshCw,
  Activity,
  TrendingUp,
  Clock,
  Menu,
  X,
  FileText,
  Heart,
  ChevronRight,
  Plus,
  Zap,
  ArrowUpRight,
  Eye,
  EyeOff
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  collection,
  query,
  orderBy,
  getDocs,
  limit,
  doc,
  getDoc,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';
import { usePoints } from './shared/PointsContext';

const HomeNew = () => {
  const navigate = useNavigate();
  const { user: userInfo, logout } = useAuth();
  const { points, loading: pointsLoading, refreshPoints } = usePoints();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [balanceVisible, setBalanceVisible] = useState(true);
  const [totalCards, setTotalCards] = useState(0);
  
  // Offer Configuration & Timers
  const [offerConfig, setOfferConfig] = useState({ active: false });
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'offers'), (docSnap) => {
      if (docSnap.exists()) {
        setOfferConfig({ id: docSnap.id, ...docSnap.data() });
      } else {
        setOfferConfig({ active: false });
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!offerConfig?.active || !offerConfig?.expiresAt) {
      setTimeLeft(null);
      return;
    }

    const calculateTimeLeft = () => {
      const difference = new Date(offerConfig.expiresAt) - new Date();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          mins: Math.floor((difference / 1000 / 60) % 60),
          secs: Math.floor((difference / 1000) % 60)
        });
      } else {
        setTimeLeft(null);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [offerConfig]);

  const isOfferCurrentlyActive = offerConfig.active && (!offerConfig.expiresAt || new Date(offerConfig.expiresAt) > new Date());

  useEffect(() => {
    async function fetchUserData() {
      if (!userInfo?.email) return;
      try {
        const userRef = doc(db, 'users', userInfo.email);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setTotalCards(userSnap.data().totalCards || 0);
        }
      } catch (err) {
        console.error('Error fetching user stats:', err);
      }
    }
    fetchUserData();
  }, [userInfo]);

  useEffect(() => {
    async function fetchActivities() {
      if (!userInfo) return;
      setActivitiesLoading(true);
      try {
        const activitiesRef = collection(db, 'users', userInfo.email, 'activities');
        const q = query(activitiesRef, orderBy('timestamp', 'desc'), limit(5));
        const querySnapshot = await getDocs(q);
        const acts = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setActivities(acts);
      } catch (err) {
        console.error('Failed to load activities:', err);
        setActivities([]);
      } finally {
        setActivitiesLoading(false);
      }
    }
    fetchActivities();
  }, [userInfo]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      alert('Logout failed.');
      setIsLoggingOut(false);
    }
  };

  const cardTypes = [
    {
      id: 'aadhaar',
      title: 'Aadhaar Card',
      description: 'Generate secure Aadhaar cards with QR codes',
      icon: <CreditCard className="w-5 h-5" />,
      path: '/aadhaar',
      cost: 25,
      gradient: 'from-blue-500 to-indigo-600',
    },
    {
      id: 'voter',
      title: 'Voter ID Card',
      description: 'Create official voter identification cards',
      icon: <UserCheck className="w-5 h-5" />,
      path: '/voter',
      cost: 25,
      gradient: 'from-emerald-500 to-teal-600',
    },
    {
      id: 'pan',
      title: 'PAN Card',
      description: 'Generate professional PAN cards',
      icon: <FileText className="w-5 h-5" />,
      path: '/pan',
      cost: 25,
      gradient: 'from-orange-500 to-amber-600',
    },
    {
      id: 'soksandesh',
      title: 'Sok Sandesh Card',
      description: 'Create memorial condolence cards',
      icon: <Heart className="w-5 h-5" />,
      path: '/soksandesh',
      cost: 25,
      gradient: 'from-rose-500 to-pink-600',
    },
  ];

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Recently';
    const now = new Date();
    const then = new Date(timestamp);
    const diff = Math.floor((now - then) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return then.toLocaleDateString();
  };

  const userName = userInfo?.email?.split('@')[0] || 'User';

  return (
    <div className="min-h-screen bg-[#f8f9fb] text-gray-900 font-sans selection:bg-black selection:text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/home')}>
              <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center text-white font-bold text-[11px] tracking-wide">
                SB
              </div>
              <span className="text-base sm:text-lg font-bold tracking-tight text-gray-900">SB Cards</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              <button
                onClick={() => navigate('/notifications')}
                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all relative"
              >
                <Bell className="w-[18px] h-[18px]" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
              </button>
              <button
                onClick={() => navigate('/settings')}
                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
              >
                <Settings className="w-[18px] h-[18px]" />
              </button>

              <div className="w-px h-6 bg-gray-200 mx-2"></div>

              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex items-center gap-2.5 px-3 py-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all disabled:opacity-50 text-sm"
              >
                <div className="w-7 h-7 bg-gray-900 rounded-full flex items-center justify-center text-white font-semibold text-[11px] uppercase">
                  {userName.charAt(0)}
                </div>
                <span className="font-medium hidden lg:inline capitalize">{userName}</span>
                {isLoggingOut ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-200 border-t-gray-600"></div>
                ) : (
                  <LogOut className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>

            {/* Mobile */}
            <div className="md:hidden flex items-center gap-1.5">
              <button
                onClick={() => navigate('/notifications')}
                className="p-2 text-gray-400 hover:text-gray-900 rounded-lg relative"
              >
                <Bell className="w-5 h-5" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white absolute w-full left-0 z-50 shadow-xl animate-slideDown">
            <div className="p-3 space-y-0.5">
              <div className="flex items-center gap-3 px-3 py-3 mb-1">
                <div className="w-9 h-9 bg-gray-900 rounded-full flex items-center justify-center text-white font-bold text-xs uppercase">
                  {userName.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-sm capitalize">{userName}</div>
                  <div className="text-xs text-gray-400">{userInfo?.email}</div>
                </div>
              </div>
              <button onClick={() => { navigate('/settings'); setIsMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium hover:bg-gray-50 rounded-lg text-gray-600 transition-colors">
                <Settings className="w-[18px] h-[18px]" /> Settings
              </button>
              <div className="border-t border-gray-100 my-1"></div>
              <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium hover:bg-red-50 rounded-lg text-red-600 transition-colors">
                <LogOut className="w-[18px] h-[18px]" /> Sign out
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

        {/* ──── DYNAMIC OFFER BANNER ──── */}
        {isOfferCurrentlyActive && (
          <div className="mb-6 bg-gradient-to-r from-indigo-900 via-indigo-800 to-purple-900 rounded-[20px] p-4 sm:p-5 shadow-xl text-white flex flex-col md:flex-row items-center justify-between animate-scale-in border border-indigo-500/30 overflow-hidden relative">
            {/* Glossy overlay */}
            <div className="absolute inset-0 bg-white opacity-[0.03] rotate-12 scale-150 transform translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>
            
            <div className="flex items-center gap-4 w-full md:w-auto relative z-10">
              <div className="bg-white/10 p-3 rounded-xl border border-white/10 backdrop-blur-md shadow-inner hidden sm:block">
                <Zap className="w-6 h-6 text-yellow-300 fill-yellow-300 animate-pulse" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-yellow-400 text-yellow-950 text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md">Live Offer</span>
                  <span className="text-indigo-200 text-xs sm:text-sm font-medium">Massive bonuses inside!</span>
                </div>
                <h3 className="text-base md:text-xl font-bold tracking-tight text-white">
                  {offerConfig.title || 'Limited Time Offer'}
                </h3>
              </div>
            </div>

            <div className="mt-4 md:mt-0 flex flex-col sm:flex-row items-center w-full md:w-auto gap-3 sm:gap-4 relative z-10">
              {timeLeft && (
                <div className="flex items-center gap-2 bg-black/30 backdrop-blur-sm px-4 py-2.5 rounded-xl border border-white/10 w-full sm:w-auto justify-center shadow-inner">
                  <Clock className="w-4 h-4 text-indigo-200" />
                  <div className="flex items-center gap-1.5 font-mono text-sm sm:text-base font-bold">
                    {timeLeft.days > 0 && <span>{String(timeLeft.days).padStart(2, '0')}<span className="text-gray-400 mx-0.5 text-xs">d</span></span>}
                    <span>{String(timeLeft.hours).padStart(2, '0')}<span className="text-gray-400 mx-0.5 text-xs">h</span></span>
                    <span>{String(timeLeft.mins).padStart(2, '0')}<span className="text-gray-400 mx-0.5 text-xs">m</span></span>
                    <span className="text-yellow-300">{String(timeLeft.secs).padStart(2, '0')}<span className="text-gray-400 mx-0.5 text-xs">s</span></span>
                  </div>
                </div>
              )}
              
              <button 
                onClick={() => navigate('/recharge')}
                className="group w-full sm:w-auto flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-yellow-950 px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-[0_0_15px_rgba(250,204,21,0.3)] hover:shadow-[0_0_25px_rgba(250,204,21,0.5)] active:scale-[0.98]"
              >
                Claim Bonus
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        )}

        {/* ──── DASHBOARD HERO ──── */}
        <div className="mb-8 sm:mb-10">

          {/* Full-width Dashboard Card */}
          <div className="relative bg-[#0f1117] rounded-2xl sm:rounded-3xl overflow-hidden">
            {/* Subtle pattern overlay */}
            <div className="absolute inset-0 opacity-[0.04]" style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
              backgroundSize: '24px 24px'
            }}></div>
            {/* Gradient accents */}
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-indigo-500/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-emerald-500/8 rounded-full blur-3xl"></div>

            <div className="relative z-10 p-5 sm:p-8">

              {/* Top row: Greeting + Actions */}
              <div className="flex items-center justify-between mb-6 sm:mb-8">
                <div>
                  <p className="text-gray-500 text-xs sm:text-sm font-medium mb-0.5">
                    Welcome back
                  </p>
                  <h1 className="text-white text-lg sm:text-xl font-semibold capitalize">
                    {userName}
                  </h1>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={refreshPoints}
                    className="p-2 text-gray-500 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                    title="Refresh"
                  >
                    <RefreshCw className={`w-4 h-4 ${pointsLoading ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={() => setBalanceVisible(!balanceVisible)}
                    className="p-2 text-gray-500 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                    title={balanceVisible ? "Hide balance" : "Show balance"}
                  >
                    {balanceVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Balance Section */}
              <div className="mb-6 sm:mb-8">
                <div className="flex items-center gap-2 mb-2">
                  <Wallet className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-[11px] sm:text-xs text-gray-500 font-medium uppercase tracking-widest">
                    Available Balance
                  </span>
                </div>
                <div className="flex items-baseline gap-1">
                  {pointsLoading ? (
                    <div className="h-10 sm:h-14 w-40 bg-white/5 rounded-lg animate-pulse"></div>
                  ) : (
                    <>
                      <span className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight tabular-nums">
                        {balanceVisible ? (points ?? 0).toLocaleString() : '••••'}
                      </span>
                      <span className="text-lg sm:text-xl text-gray-600 font-medium ml-1">pts</span>
                    </>
                  )}
                </div>
              </div>

              {/* Metrics Row */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl px-3 sm:px-4 py-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Activity className="w-3 h-3 text-blue-400" />
                    <span className="text-[10px] sm:text-[11px] text-gray-500 font-medium uppercase tracking-wider">Generated</span>
                  </div>
                  <span className="text-white font-bold text-lg sm:text-xl">{totalCards}</span>
                </div>
                <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl px-3 sm:px-4 py-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <TrendingUp className="w-3 h-3 text-emerald-400" />
                    <span className="text-[10px] sm:text-[11px] text-gray-500 font-medium uppercase tracking-wider">Success</span>
                  </div>
                  <span className="text-white font-bold text-lg sm:text-xl">100%</span>
                </div>
                <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl px-3 sm:px-4 py-3">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Clock className="w-3 h-3 text-violet-400" />
                    <span className="text-[10px] sm:text-[11px] text-gray-500 font-medium uppercase tracking-wider">Avg Time</span>
                  </div>
                  <span className="text-white font-bold text-lg sm:text-xl">&lt;30s</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/recharge')}
                  className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 bg-white text-gray-900 font-semibold text-sm rounded-xl hover:bg-gray-100 transition-all active:scale-[0.98] shadow-lg shadow-white/10"
                >
                  <Plus className="w-4 h-4" />
                  Add Points
                </button>
                <button
                  onClick={() => navigate('/recharge')}
                  className="hidden sm:inline-flex items-center justify-center gap-2 px-5 py-3 text-gray-500 font-medium text-sm rounded-xl hover:text-white hover:bg-white/[0.06] transition-all"
                >
                  View Transactions
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ──── AVAILABLE TOOLS ──── */}
        <div className="mb-10 sm:mb-14">
          <div className="flex items-center justify-between mb-5 sm:mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">Available Tools</h2>
              <p className="text-xs sm:text-sm text-gray-400 mt-0.5">Select a tool to generate documents</p>
            </div>
            <span className="text-[10px] sm:text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full border border-gray-200">
              {cardTypes.length} Tools
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {cardTypes.map((card) => (
              <div
                key={card.id}
                onClick={() => navigate(card.path)}
                onMouseEnter={() => {
                  if (card.id === 'aadhaar') import('./Aadhaar/CardGeneratorNew').catch(() => { });
                  else if (card.id === 'voter') import('./Voter/VoterFormNew').catch(() => { });
                  else if (card.id === 'pan') import('./Pan/PanCardGeneratorNew').catch(() => { });
                  else if (card.id === 'soksandesh') import('./SokSandesh/SokSandeshFormNew').catch(() => { });
                }}
                className="group relative bg-white border border-gray-200 rounded-2xl p-5 sm:p-6 hover:border-gray-300 hover:shadow-lg transition-all cursor-pointer active:scale-[0.98]"
              >
                {/* Gradient accent line */}
                <div className={`absolute top-0 left-6 right-6 h-0.5 bg-gradient-to-r ${card.gradient} rounded-full opacity-0 group-hover:opacity-100 transition-opacity`}></div>

                <div className={`w-11 h-11 sm:w-12 sm:h-12 bg-gradient-to-br ${card.gradient} rounded-xl flex items-center justify-center mb-4 sm:mb-5 text-white shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all`}>
                  {card.icon}
                </div>

                <div className="mb-5 sm:mb-6">
                  <h3 className="font-bold text-gray-900 text-base sm:text-lg mb-1.5">{card.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">{card.description}</p>
                </div>

                <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 bg-gray-50 px-2.5 py-1 rounded-lg border border-gray-100">
                    <Wallet className="w-3 h-3" /> {card.cost} pts
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center transform translate-x-2 group-hover:translate-x-0 opacity-0 group-hover:opacity-100 transition-all shadow-md">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ──── RECENT ACTIVITY ──── */}
        <div>
          <div className="flex items-center justify-between mb-5 sm:mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">Recent Activity</h2>
              <p className="text-xs sm:text-sm text-gray-400 mt-0.5">Your latest generated documents</p>
            </div>
            {activities.length > 0 && (
              <button className="text-xs sm:text-sm font-medium text-gray-500 hover:text-black transition-colors px-3 py-1.5 hover:bg-gray-100 rounded-lg">
                View All
              </button>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            {activitiesLoading ? (
              <div className="p-8 sm:p-12 text-center">
                <div className="animate-spin rounded-full h-7 w-7 border-2 border-gray-200 border-t-gray-900 mx-auto mb-4"></div>
                <p className="text-sm text-gray-500">Loading your history...</p>
              </div>
            ) : activities.length === 0 ? (
              <div className="p-12 sm:p-16 text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-gray-100">
                  <Activity className="w-8 h-8 sm:w-10 sm:h-10 text-gray-300" />
                </div>
                <h3 className="text-gray-900 font-semibold mb-2 text-base sm:text-lg">No activities yet</h3>
                <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">Your generated cards will appear here. Start by selecting a tool above!</p>
                <button
                  onClick={() => navigate('/aadhaar')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 transition-colors text-sm shadow-md"
                >
                  <Zap className="w-4 h-4" />
                  Generate Your First Card
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs sm:text-sm">
                  <thead className="bg-gray-50/80 border-b border-gray-100 text-[10px] sm:text-xs uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-4 sm:px-6 py-3.5 sm:py-4 font-medium">Document</th>
                      <th className="px-4 sm:px-6 py-3.5 sm:py-4 font-medium hidden sm:table-cell">Holder Name</th>
                      <th className="px-4 sm:px-6 py-3.5 sm:py-4 font-medium">Date</th>
                      <th className="px-4 sm:px-6 py-3.5 sm:py-4 font-medium text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {activities.map((activity, index) => (
                      <tr key={index} className="hover:bg-gray-50/70 transition-colors">
                        <td className="px-4 sm:px-6 py-3.5 sm:py-4 font-medium text-gray-900">{activity.cardType}</td>
                        <td className="px-4 sm:px-6 py-3.5 sm:py-4 text-gray-500 hidden sm:table-cell">{activity.holderName}</td>
                        <td className="px-4 sm:px-6 py-3.5 sm:py-4 text-gray-400 text-[10px] sm:text-xs">{formatTimeAgo(activity.timestamp)}</td>
                        <td className="px-4 sm:px-6 py-3.5 sm:py-4 text-right">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-medium bg-green-50 text-green-700 border border-green-100">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                            Generated
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default HomeNew;
